"use client";

import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { Box, Typography, Tooltip, Chip, Link } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import VisibilityIcon from "@mui/icons-material/Visibility";

function AllQuestions({ data }) {
  const navigate = useNavigate();
  const [tagDescriptions, setTagDescriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function getTags() {
      setIsLoading(true);
      try {
        const response = await axios.get("/api/tags", {
          baseURL: process.env.REACT_APP_FORUM_API,
        });
        setTagDescriptions(response.data || []);
      } catch (error) {
        console.error("Error fetching tags:", error);
      } finally {
        setIsLoading(false);
      }
    }
    getTags();
  }, []);

  const truncate = (str, n) =>
    str?.length > n ? str.substr(0, n - 1) + "..." : str;

  const getTagDescription = (tagName) => {
    const tag = tagDescriptions.find((t) => t.name === tagName);
    return tag?.description
      ? truncate(tag.description, 150)
      : "Description not available";
  };

  const createdAt = moment(data?.created_at);
  const displayDate =
    moment().diff(createdAt, "hours") < 24
      ? createdAt.fromNow()
      : createdAt.format("MMM DD [at] HH:mm");

  const answerCount = data?.answerDetails?.length || 0;
  const isVerified = data?.verification?.verified || false;
  const isBianVerified =
    data?.verification?.verificationType === "admin" || false;
  const voteCount = data?.voteCount || data?.vote || 0;
  const viewCount = data?.viewCount || 0;

  const getTextPreview = (htmlContent) => {
    if (!htmlContent) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    return truncate(textContent, 200);
  };

  const textPreview = getTextPreview(data?.body);

  const calculateTotalVotes = () => {
    const questionVotes = voteCount || 0;
    const answerVotes =
      data?.answerDetails?.reduce(
        (sum, answer) => sum + (answer.voteCount || 0),
        0
      ) || 0;
    return questionVotes + answerVotes;
  };

  const totalVotes = calculateTotalVotes();
  const hasHighVotes = totalVotes >= 5;

  // Check for admin and user verifications
  const hasAdminVerification = data.answerDetails?.some(
    (a) =>
      a.verifications &&
      a.verifications.some((v) => v.verificationType === "admin")
  );
  const hasUserVerification = data.answerDetails?.some(
    (a) =>
      a.verifications &&
      a.verifications.some((v) => v.verificationType === "user")
  );

  // Enhanced Verification Badge Component
  const VerificationBadge = ({ type, size = "small" }) => {
    const isAdmin = type === "admin";
    const isSmall = size === "small";

    return (
      <Tooltip
        title={isAdmin ? "Admin Verified Answer" : "Community Verified Answer"}
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: isSmall ? 20 : 24,
            height: isSmall ? 20 : 24,
            borderRadius: "50%",
            background: isAdmin ? "#2e7d32" : "#1976d2",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            border: "2px solid #fff",
            p: 0.5,
            ml: 0.5,
          }}
        >
          {isAdmin ? (
            <DoneAllIcon
              sx={{
                color: "#fff",
                fontSize: isSmall ? 12 : 16,
                fontWeight: "bold",
                p: 0.5,
              }}
            />
          ) : (
            <DoneIcon
              sx={{
                color: "#fff",
                fontSize: isSmall ? 12 : 16,
                fontWeight: "bold",
                p: 0.5,
              }}
            />
          )}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: "4px",
        p: { xs: 2, md: 2.5 },
        bgcolor: "#f8f9fa",
        width: "100%",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: { xs: 0.5, md: 1 },
        }}
      >
        {/* Stats Section - Left Side */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            pr: 0.5,
            minWidth: "60px",
          }}
        >
          {/* Votes */}
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 0.5,
                gap: 0.5,
              }}
            >
              <ThumbUpAltIcon sx={{ color: "#6a737c", fontSize: 20 }} />
              <Typography
                sx={{ fontSize: "16px", fontWeight: "500", color: "#3b4045" }}
              >
                {totalVotes}
              </Typography>
              <Typography sx={{ fontSize: "16px", color: "#6a737c" }}>
                votes
              </Typography>
            </Box>
          </Box>

          {/* Answers with verification */}
          <Box sx={{ textAlign: "center", position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 0.5,
                gap: 0.5,
              }}
            >
              <QuestionAnswerIcon
                sx={{
                  color: answerCount > 0 ? "#2e7d32" : "#6a737c",
                  fontSize: 20,
                }}
              />
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: answerCount > 0 ? "#2e7d32" : "#3b4045",
                }}
              >
                {answerCount}
              </Typography>
              <Typography sx={{ fontSize: "16px", color: "#6a737c" }}>
                answers
              </Typography>
            </Box>

            {/* Verification badges next to answer count */}
            {/* {(hasAdminVerification || hasUserVerification) && (
              <Box
                sx={{
                  position: "absolute",
                  top: -5,
                  right: -10,
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {hasAdminVerification && <VerificationBadge type="admin" />}
                {hasUserVerification && <VerificationBadge type="user" />}
              </Box>
            )} */}
          </Box>

          {/* Views */}
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                justifyItems: "center",
                mb: 0.5,
                gap: 0.5,
              }}
            >
              <VisibilityIcon sx={{ color: "#6a737c", fontSize: 20 }} />
              <Typography
                sx={{ fontSize: "16px", fontWeight: "500", color: "#3b4045" }}
              >
                {viewCount}
              </Typography>
              <Typography sx={{ fontSize: "16px", color: "#6a737c" }}>
                views
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content Section - Right Side */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title with verification badges */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "18px", md: "20px" },
                fontWeight: 500,
                color: "#0074cc",
                "&:hover": {
                  color: "#0a95ff",
                },
              }}
            >
              <Link
                component={RouterLink}
                to={`/question?q=${data?._id}`}
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                {data?.title}
              </Link>
            </Typography>

            {/* Verification badges next to title */}
            <Box sx={{ display: "flex", ml: "auto" }}>
              {hasAdminVerification && (
                <VerificationBadge type="admin" size="medium" />
              )}
              {hasUserVerification && (
                <VerificationBadge type="user" size="medium" />
              )}
            </Box>
          </Box>

          {/* Question preview */}
          <Typography
            sx={{
              fontSize: "14px",
              color: "#3b4045",
              mb: 1.5,
              lineHeight: 1.4,
            }}
          >
            {textPreview}
          </Typography>

          {/* Tags and user info */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: 1,
            }}
          >
            {/* Tags */}
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {isLoading ? (
                <Typography sx={{ fontSize: "12px", color: "#6a737c" }}>
                  Loading tags...
                </Typography>
              ) : (
                data?.tagDetails?.map((tag, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <>
                        <strong>{tag.name}</strong>
                        <br />
                        {getTagDescription(tag.name)}
                      </>
                    }
                  >
                    <Chip
                      label={tag.name}
                      size="small"
                      sx={{
                        fontSize: "12px",
                        height: "22px",
                        backgroundColor: "#e1ecf4",
                        color: "#39739d",
                        borderRadius: "3px",
                      }}
                    />
                  </Tooltip>
                ))
              )}
            </Box>

            {/* User info */}
            <Typography
              sx={{ fontSize: "12px", color: "#6a737c", whiteSpace: "nowrap" }}
            >
              asked by{" "}
              <Link
                // component={RouterLink}
                // to={`/user/${data?.user?._id}`}
                sx={{
                  color: "#0074cc",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#0a95ff",
                  },
                }}
              >
                { data?.user?.firstName ? `${data?.user?.firstName}  ${data?.user?.lastName}` : "Unknown User"}
                {/* {data?.user?.userName || "Unknown User"} */}
              </Link>{" "}
              {displayDate}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AllQuestions;

// "use client"

// import { useEffect, useState } from "react"
// import { Link as RouterLink, useNavigate } from "react-router-dom"
// import moment from "moment"
// import axios from "axios"
// import { Box, Stack, Typography, Tooltip, Chip, Link } from "@mui/material"
// import VerifiedUserIcon from "@mui/icons-material/VerifiedUser"
// import DoneIcon from "@mui/icons-material/Done"
// import DoneAllIcon from "@mui/icons-material/DoneAll"

// function AllQuestions({ data }) {
//   const navigate = useNavigate()
//   const [tagDescriptions, setTagDescriptions] = useState([])
//   const [isLoading, setIsLoading] = useState(true)

//   useEffect(() => {
//     async function getTags() {
//       setIsLoading(true)
//       try {
//         const response = await axios.get("/api/tags", {
//           baseURL: process.env.REACT_APP_FORUM_API,
//         })
//         setTagDescriptions(response.data || [])
//       } catch (error) {
//         console.error("Error fetching tags:", error)
//       } finally {
//         setIsLoading(false)
//       }
//     }
//     getTags()
//   }, [])

//   const truncate = (str, n) => (str?.length > n ? str.substr(0, n - 1) + "..." : str)

//   const getTagDescription = (tagName) => {
//     const tag = tagDescriptions.find((t) => t.name === tagName)
//     return tag?.description ? truncate(tag.description, 150) : "Description not available"
//   }

//   const createdAt = moment(data?.created_at)
//   const displayDate =
//     moment().diff(createdAt, "hours") < 24 ? createdAt.fromNow() : createdAt.format("MMM DD [at] HH:mm")

//   const answerCount = data?.answerDetails?.length || 0
//   const isVerified = data?.verification?.verified || false
//   const isBianVerified = data?.verification?.verificationType === "admin" || false
//   const voteCount = data?.voteCount || data?.vote || 0
//   const viewCount = data?.viewCount || 0

//   const getTextPreview = (htmlContent) => {
//     if (!htmlContent) return ""

//     const tempDiv = document.createElement("div")
//     tempDiv.innerHTML = htmlContent
//     const textContent = tempDiv.textContent || tempDiv.innerText || ""
//     return truncate(textContent, 200)
//   }

//   const textPreview = getTextPreview(data?.body)

//   const calculateTotalVotes = () => {
//     const questionVotes = voteCount || 0
//     const answerVotes = data?.answerDetails?.reduce((sum, answer) => sum + (answer.voteCount || 0), 0) || 0
//     return questionVotes + answerVotes
//   }

//   const totalVotes = calculateTotalVotes()
//   const hasHighVotes = totalVotes >= 5

//   return (
//     <Box
//       sx={{
//         borderBottom: "1px solid #e3e6e8",
//         p: 2,
//         bgcolor: "white",
//         width: "100%",
//         "&:hover": {
//           bgcolor: "#f8f9fa",
//         },
//       }}
//     >
//       <Box
//         sx={{
//           display: "flex",
//           flexDirection: { xs: "column", md: "row" },
//           alignItems: { xs: "flex-start", md: "flex-start" },
//         }}
//       >
//         {/* Stats (top row on mobile, left column on desktop) */}
//         <Box
//           sx={{
//             minWidth: { xs: "auto", md: "70px" },
//             textAlign: { xs: "left", md: "right" },
//             mb: { xs: 1, md: 0 },
//             mr: { md: 2 },
//           }}
//         >
//           <Stack
//             direction={{ xs: "row", md: "column" }}
//             spacing={{ xs: 2, md: 1 }}
//             alignItems={{ xs: "center", md: "flex-end" }}
//           >
//             {/* Votes */}
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography
//                 sx={{
//                   fontSize: { xs: "12px", md: "12px" },
//                   color: hasHighVotes ? "#f48024" : "#0c0d0e",
//                   fontWeight: "bold",
//                 }}
//               >
//                 {totalVotes}
//               </Typography>
//               <Typography
//                 sx={{
//                   fontSize: { xs: "12px", md: "12px" },
//                   color: hasHighVotes ? "#f48024" : "#6a737c",
//                 }}
//               >
//                 votes
//               </Typography>
//             </Stack>

//             {/* Answers */}
//             <Box
//               sx={{
//                 display: "flex",
//                 flexDirection: "row",
//                 alignItems: "center",
//                 justifyContent: { xs: "flex-start", md: "flex-end" },
//                 px: 0.5,
//                 py: 0.25,
//                 borderRadius: "3px",
//                 border: answerCount > 0 && !(isVerified || isBianVerified) ? "1px solid #2f6f44" : "none",
//                 bgcolor: (isVerified || isBianVerified) && answerCount > 0 ? "#2f6f44" : "transparent",
//                 position: "relative",
//               }}
//             >
//               <Typography
//                 sx={{
//                   fontSize: { xs: "12px", md: "12px" },
//                   fontWeight: "bold",
//                   mr: 0.5,
//                   color:
//                     (isVerified || isBianVerified) && answerCount > 0
//                       ? "#fff"
//                       : answerCount > 0
//                         ? "#2f6f44"
//                         : "#6a737c",
//                 }}
//               >
//                 {answerCount}
//               </Typography>
//               <Typography
//                 sx={{
//                   fontSize: { xs: "12px", md: "12px" },
//                   color:
//                     (isVerified || isBianVerified) && answerCount > 0
//                       ? "#fff"
//                       : answerCount > 0
//                         ? "#2f6f44"
//                         : "#6a737c",
//                 }}
//               >
//                 {answerCount === 1 ? "answer" : "answers"}
//               </Typography>
//               <Box
//                 sx={{
//                   position: "absolute",
//                   top: -10,
//                   right: -10,
//                   display: "flex",
//                   flexDirection: "row",
//                   alignItems: "center",
//                   visibility: data.answerDetails.some(
//                     (a) => a?.verification?.verified || (a?.verifications && a.verifications.length > 0),
//                   )
//                     ? "visible"
//                     : "hidden",
//                 }}
//               >
//                 {data.answerDetails.some(
//                   (a) => a.verifications && a.verifications.some((v) => v.verificationType === "admin"),
//                 ) && (
//                   <Tooltip title="Admin verified">
//                     <Box
//                       sx={{
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         position: "relative",
//                         mr: 0.5

//                       }}
//                     >
//                       <VerifiedUserIcon
//                         sx={{
//                           color: "#2f6f44",
//                           fontSize: { xs: 16, sm: 20 },

//                         }}
//                       />
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           width: "60%",
//                           height: "60%",

//                         }}
//                       >
//                         <DoneAllIcon
//                           sx={{
//                             color: "#fff",
//                             fontSize: { xs: 8, sm: 10 },
//                             p: 0.5
//                           }}
//                         />
//                       </Box>
//                     </Box>
//                   </Tooltip>
//                 )}
//                 {data.answerDetails.some(
//                   (a) => a.verifications && a.verifications.some((v) => v.verificationType === "user"),
//                 ) && (
//                   <Tooltip title="User verified">
//                     <Box
//                       sx={{
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         position: "relative",
//                       }}
//                     >
//                       <VerifiedUserIcon
//                         sx={{
//                           color: "#0000FF",
//                           fontSize: { xs: 16, sm: 20 },
//                         }}
//                       />
//                       <Box
//                         sx={{
//                           position: "absolute",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           width: "60%",
//                           height: "60%",
//                         }}
//                       >
//                         <DoneIcon
//                           sx={{
//                             color: "#fff",
//                             fontSize: { xs: 8, sm: 10 },
//                             p: 1
//                           }}
//                         />
//                       </Box>
//                     </Box>
//                   </Tooltip>
//                 )}
//               </Box>
//             </Box>

//             {/* Views */}
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography
//                 sx={{
//                   fontSize: { xs: "12px", md: "12px" },
//                   color: "#0c0d0e",
//                   fontWeight: "bold",
//                 }}
//               >
//                 {viewCount}
//               </Typography>
//               <Typography
//                 sx={{
//                   fontSize: { xs: "12px", md: "12px" },
//                   color: "#6a737c",
//                 }}
//               >
//                 views
//               </Typography>
//             </Stack>
//           </Stack>
//         </Box>

//         {/* Content (below stats on mobile, right column on desktop) */}
//         <Box sx={{ flex: { md: "1 1 auto" }, overflow: "hidden", minWidth: 0 }}>
//           <Typography
//             variant="h3"
//             sx={{
//               fontSize: { xs: "16px", md: "17px" },
//               fontWeight: 400,
//               lineHeight: 1.3,
//               mb: 0.75,
//               color: "#1a1a1a",
//               display: "flex",
//               alignItems: "center",
//             }}
//           >
//             {hasHighVotes && (
//               <Tooltip title={`Popular question with ${totalVotes} total votes`}>
//                 <Box
//                   component="span"
//                   sx={{
//                     color: "#f48024",
//                     mr: 1,
//                     display: "flex",
//                     alignItems: "center",
//                     fontSize: { xs: "14px", md: "16px" },
//                   }}
//                 >
//                   ★
//                 </Box>
//               </Tooltip>
//             )}
//             <Link
//               component={RouterLink}
//               to={`/question?q=${data?._id}`}
//               sx={{
//                 color: "#0074cc",
//                 textDecoration: "none",
//                 "&:hover": {
//                   color: "#0a95ff",
//                 },
//               }}
//             >
//               {data?.title}
//             </Link>
//           </Typography>

//           <Typography
//             sx={{
//               fontSize: { xs: "12px", md: "13px" },
//               color: "#3b4045",
//               mb: 1,
//               display: "-webkit-box",
//               WebkitLineClamp: 2,
//               WebkitBoxOrient: "vertical",
//               overflow: "hidden",
//               lineHeight: 1.3,
//             }}
//           >
//             {textPreview}
//           </Typography>

//           <Stack
//             direction="row"
//             justifyContent="space-between"
//             alignItems="center"
//             flexWrap="wrap"
//             sx={{ mt: { xs: 1, md: 0 } }}
//           >
//             {/* Tags */}
//             <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: { xs: 1, sm: 0 } }}>
//               {isLoading ? (
//                 <Typography
//                   sx={{
//                     fontSize: { xs: "11px", md: "12px" },
//                     color: "#6a737c",
//                   }}
//                 >
//                   Loading tags...
//                 </Typography>
//               ) : (
//                 data?.tagDetails?.map((tag, index) => (
//                   <Tooltip
//                     key={index}
//                     title={
//                       <>
//                         <strong>{tag.name}</strong>
//                         <br />
//                         {getTagDescription(tag.name)}
//                       </>
//                     }
//                   >
//                     <Chip
//                       label={tag.name}
//                       sx={{
//                         fontSize: { xs: "11px", md: "12px" },
//                         height: { xs: "22px", md: "auto" },
//                         backgroundColor: "#e3e6e8",
//                         color: "#0c0d0e",
//                       }}
//                     />
//                   </Tooltip>
//                 ))
//               )}
//             </Stack>

//             {/* User */}
//             <Typography sx={{ fontSize: { xs: "11px", md: "12px" }, color: "#6a737c" }}>
//               asked by{" "}
//               <Link
//                 component={RouterLink}
//                 to={`/user/${data?.user?._id}`}
//                 sx={{
//                   color: "#0074cc",
//                   textDecoration: "none",
//                   "&:hover": {
//                     color: "#0a95ff",
//                   },
//                 }}
//               >
//                 {data?.user?.name || "Unknown User"}
//               </Link>{" "}
//               {displayDate}
//             </Typography>
//           </Stack>
//         </Box>
//       </Box>
//     </Box>
//   )
// }

// export default AllQuestions

// "use client"

// import { useEffect, useState } from "react";
// import ReactHtmlParser from "react-html-parser";
// import { Link as RouterLink, useNavigate } from "react-router-dom";
// import moment from "moment";
// import axios from "axios";
// import {
//   Box,
//   Stack,
//   Typography,
//   Tooltip,
//   Chip,
//   Button,
//   Link,
// } from "@mui/material";

// function AllQuestions({ data }) {
//   const navigate = useNavigate();
//   const [tagDescriptions, setTagDescriptions] = useState([]);

//   useEffect(() => {
//     async function getTags() {
//       try {
//         const response = await axios.get("/api/tags", {
//           baseURL: process.env.REACT_APP_FORUM_API,
//         });
//         setTagDescriptions(response.data || []);
//       } catch (error) {
//         console.error("Error fetching tags:", error);
//       }
//     }
//     getTags();
//   }, []);

//   const truncate = (str, n) => (str?.length > n ? str.substr(0, n - 1) + "..." : str);

//   const getTagDescription = (tagName) => {
//     const tag = tagDescriptions.find((t) => t.name === tagName);
//     return tag?.description ? truncate(tag.description, 150) : "Description not available";
//   };

//   const createdAt = moment(data?.created_at);
//   const displayDate =
//     moment().diff(createdAt, "hours") < 24 ? createdAt.fromNow() : createdAt.format("MMM DD [at] HH:mm");

//   const answerCount = data?.answerDetails?.length || 0;
//   const isVerified = data?.verified || data?.bian_verified;

//   const handleAddQuestionClick = () => {
//     navigate("/add-question");
//   };

//   return (
//     <Box
//       sx={{
//         borderBottom: "1px solid #e3e6e8",
//         p: 2,
//         bgcolor: "white",
//         width: "100%",
//       }}
//     >
//       {/* Ask Question Button */}
//       {/* <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
//         <Button
//           onClick={handleAddQuestionClick}
//           sx={{
//             backgroundColor: "#0a95ff",
//             color: "white",
//             border: "none",
//             padding: "8px 16px",
//             borderRadius: "9999px",
//             fontSize: "14px",
//             fontWeight: 500,
//             boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
//             transition: "background-color 0.2s",
//             textTransform: "none",
//             "&:hover": {
//               backgroundColor: "#0074cc",
//             },
//           }}
//         >
//           Ask Question
//         </Button>
//       </Box> */}

//       {/* Main Content */}
//       <Stack direction="row" spacing={2}>
//         {/* Left side - stats */}
//         <Box sx={{ minWidth: "70px", textAlign: "right" }}>
//           <Stack spacing={1} alignItems="flex-end">
//             {/* Votes */}
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography
//                 sx={{
//                   fontSize: "12px",
//                   color: "#0c0d0e",
//                   fontWeight: "bold",
//                 }}
//               >
//                 {data?.vote || 0}
//               </Typography>
//               <Typography
//                 sx={{
//                   fontSize: "12px",
//                   color: "#6a737c",
//                 }}
//               >
//                 votes
//               </Typography>
//             </Stack>

//             {/* Answers */}
//             <Box
//               sx={{
//                 display: "flex",
//                 flexDirection: "row",
//                 alignItems: "center",
//                 justifyContent: "flex-end",
//                 px: 0.20,
//                 py: 0.10,
//                 // color: answerCount > 0 ? (isVerified ? "#fff" : "#2f6f44") : "#6a737c",
//                 // backgroundColor: isVerified ? "#2f6f44" : "transparent",
//                 // border: answerCount > 0 && !isVerified ? "1px solid #2f6f44" : "none",
//                 borderRadius: "3px",
//               }}
//             >
//               <Typography
//                 sx={{
//                   fontSize: "12px",
//                   fontWeight: "bold",
//                   mr: 0.5,
//                 }}
//               >
//                 {answerCount}
//               </Typography>
//               <Typography sx={{ fontSize: "12px" }}>
//                 {answerCount === 1 ? "answer" : "answers"}
//               </Typography>
//             </Box>

//             {/* Views */}
//             <Stack direction="row" spacing={0.5} alignItems="center">
//               <Typography
//                 sx={{
//                   fontSize: "12px",
//                   color: "#0c0d0e",
//                   fontWeight: "bold",
//                 }}
//               >
//                 {data?.views || 2}
//               </Typography>
//               <Typography
//                 sx={{
//                   fontSize: "12px",
//                   color: "#6a737c",
//                 }}
//               >
//                 views
//               </Typography>
//             </Stack>
//           </Stack>
//         </Box>

//         {/* Right side - content */}
//         <Box sx={{ flex: "1 1 auto", overflow: "hidden", minWidth: 0 }}>
//           <Typography
//             variant="h3"
//             sx={{
//               fontSize: "17px",
//               fontWeight: 400,
//               lineHeight: 1.3,
//               mb: 0.75,
//               color: "#1a1a1a",
//             }}
//           >
//             <Link
//               component={RouterLink}
//               to={`/question?q=${data?._id}`}
//               sx={{
//                 color: "#0074cc",
//                 textDecoration: "none",
//                 "&:hover": {
//                   color: "#0a95ff",
//                 },
//               }}
//             >
//               {data?.title}
//             </Link>
//           </Typography>

//           <Typography
//             sx={{
//               fontSize: "13px",
//               color: "#3b4045",
//               mb: 1,
//               display: "-webkit-box",
//               WebkitLineClamp: 2,
//               WebkitBoxOrient: "vertical",
//               overflow: "hidden",
//               lineHeight: 1.3,
//             }}
//           >
//             {ReactHtmlParser(data?.body || "")}
//           </Typography>

//           <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
//             {/* Tags */}
//             <Stack direction="row" spacing={0.5} flexWrap="wrap">
//               {data?.tagDetails?.map((tag, index) => (
//                 <Tooltip
//                   key={index}
//                   title={
//                     <>
//                       <strong>{tag.name}</strong>
//                       <br />
//                       {getTagDescription(tag.name)}
//                     </>
//                   }
//                 >
//                   <Chip
//                     label={tag.name}
//                     onClick={() => navigate(`/tags/${tag.name}`)}
//                     sx={{
//                       backgroundColor: "#e1ecf4",
//                       color: "#39739d",
//                       fontSize: "12px",
//                       height: "24px",
//                       cursor: "pointer",
//                       "&:hover": {
//                         backgroundColor: "#d0e3f1",
//                       },
//                     }}
//                   />
//                 </Tooltip>
//               ))}
//             </Stack>

//             {/* Author and Date */}
//             <Typography
//               sx={{
//                 fontSize: "12px",
//                 color: "#6a737c",
//               }}
//             >
//               {data?.user?.userName || "Anonymous"}, {displayDate}
//             </Typography>
//           </Stack>
//         </Box>
//       </Stack>
//     </Box>
//   );
// }

// export default AllQuestions;
